import React, { useEffect, useMemo, useRef, useState } from 'react';
import './TableColumnMenu.css';

export default function TableColumnMenu({
  anchorElement,
  activeColumnKey,
  columns,
  onClose,
  onMove,
  onToggle,
  onWidthChange,
  onReset,
}) {
  const menuRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const activeColumn = useMemo(
    () => columns.find(column => column.key === activeColumnKey),
    [columns, activeColumnKey]
  );
  const totalVisible = useMemo(
    () => columns.filter(column => column.visible !== false).length,
    [columns]
  );

  useEffect(() => {
    if (!anchorElement || !activeColumn) return;
    const rect = anchorElement.getBoundingClientRect();
    const menuWidth = 320;
    const top = rect.bottom + window.scrollY + 8;
    const desiredLeft = rect.left + window.scrollX + rect.width - menuWidth;
    const left = Math.max(16, desiredLeft);
    setPosition({ top, left });

    const handleClickOutside = event => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        anchorElement &&
        !anchorElement.contains(event.target)
      ) {
        onClose();
      }
    };

    const handleEsc = event => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('keydown', handleEsc);
    window.addEventListener('resize', onClose);
    window.addEventListener('scroll', onClose, true);
    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('keydown', handleEsc);
      window.removeEventListener('resize', onClose);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [anchorElement, activeColumn, onClose]);

  if (!activeColumn || !anchorElement) return null;

  const columnIndex = columns.findIndex(column => column.key === activeColumn.key);
  const canMoveLeft = columnIndex > 0;
  const canMoveRight = columnIndex < columns.length - 1;

  return (
    <div className="table-column-menu" style={position} ref={menuRef}>
      <header className="table-column-menu__header">
        <div>
          <h4>{activeColumn.label}</h4>
          <p>Ajusta visibilidad, ancho y orden de columnas.</p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={onReset}>
          Restablecer
        </button>
      </header>
      <section className="table-column-menu__group">
        <span className="table-column-menu__group-title">Ancho</span>
        <div className="table-column-menu__width">
          <input
            type="range"
            min={activeColumn.minWidth || 120}
            max={activeColumn.maxWidth || 420}
            value={Math.round(activeColumn.width)}
            onChange={event => onWidthChange(activeColumn.key, Number(event.target.value))}
          />
          <span>{Math.round(activeColumn.width)} px</span>
        </div>
      </section>
      <section className="table-column-menu__group">
        <span className="table-column-menu__group-title">Orden</span>
        <div className="table-column-menu__order">
          <button
            type="button"
            className="table-column-menu__order-btn"
            onClick={() => onMove(columnIndex, columnIndex - 1)}
            disabled={!canMoveLeft}
            aria-label="Mover a la izquierda"
          >
            ←
          </button>
          <button
            type="button"
            className="table-column-menu__order-btn"
            onClick={() => onMove(columnIndex, columnIndex + 1)}
            disabled={!canMoveRight}
            aria-label="Mover a la derecha"
          >
            →
          </button>
        </div>
      </section>
      <section className="table-column-menu__group">
        <span className="table-column-menu__group-title">Visibilidad</span>
        <div className="table-column-menu__list">
          {columns.map(column => {
            const checked = column.visible !== false;
            const disabled = checked && totalVisible <= 1;
            return (
              <label key={column.key} className="table-column-menu__option">
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => onToggle(column.key)}
                />
                <span>{column.label}</span>
              </label>
            );
          })}
        </div>
      </section>
    </div>
  );
}

